import React, { useCallback, useState } from 'react';
import useRootSelector from 'common/hooks/useRootSelector';
import { FormattedMessage } from 'react-intl';
import { List as ImmutableList } from 'immutable';
import { Grid, Button, makeStyles, Theme } from '@material-ui/core';
import * as selectors from 'common/components/PersonProfile/state/selectors';
import { IPastDue, IProfileInfoImt } from 'common/components/PersonProfile/interfaces';
import { IMemberAlertImt } from 'modules/front-desk/interfaces';
import { ITableParams } from 'common/interfaces/table';
import { AlertCondition } from 'common/interfaces/alerts';
import {
  BillingScheduleModal,
  PastDuesResolutionModal,
  PaymentMethodsModal,
} from 'common/components/PersonProfile/modals';
import messages from 'common/components/PersonProfile/messages';
import commonMessages from 'common/messages/messages';
import { CartUnitTypes, IInvoiceDetailsDto, InvoiceStatus } from 'common/interfaces/invoices';
import { ADD_TOP_UP_BALANCE } from 'common/constants/delayedActionsKeys';
import { PeakModuleForNewPersonType } from 'common/interfaces/steps';
import { useAppDispatch } from 'store/hooks';
import { selectSelectedRegisterId } from 'modules/pos-kiosk/state/register/selectors';
import StorageServices from 'services/storage';
import { snackbar } from 'common/utils/snackbarUtils';
import TopUpBalanceModal from 'common/components/InvoiceOperating/InvoiceModals/TopUpBalanceModal/TopUpBalanceModal';
import * as actions from 'common/state/invoice/actions';
import { selectInvoicesForRegisterList } from 'common/state/invoice/selectors';
import TopUpRewardsModal from 'common/components/PersonProfile/modals/TopUpRewardsModal/TopUpRewardsModal';
import BalanceHistoryModal from '../../modals/BalanceHistoryModal/BalanceHistoryModal';
import InvoiceDetailsModal from '../Payments/InvoiceDetailsModal/InvoiceDetailsModal';
import { fetchPersonBalanceHistory, fetchPersonRewardsHistory } from '../../state/actions';
import RewardsHistoryModal from '../../modals/RewardsHistoryModal/RewardsHistoryModal';
import DialogComponent from 'common/components/DialogComponent/DialogComponent';
import PaymentStep from '../PaymentStep/PaymentStep';

const useStyles = makeStyles((theme: Theme) => ({
  selectPackage: {
    color: theme.palette.secondary.main,
    width: '100%',
    height: '100%',
  },
  showPanel: {
    position: 'absolute',
  },
  checkInBlock: {
    width: '50%',
    paddingRight: '16px',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    '&:only-of-type': {
      width: '100%',
    },
  },
  customPaper: {
    '& .MuiDialog-container>.MuiDialog-paperScrollPaper': {
      overflowY: 'hidden',
    },
  },
}));

interface IProps {
  profile: IProfileInfoImt;
  module: PeakModuleForNewPersonType;
  openPOSPanel: () => void;
  handlePOSPanelChange: (isOpen: boolean) => void;
  handleNewMemberPanelChange: (isOpen: boolean) => void;
  handleChangePackagePlanChange: (isOpen: boolean) => void;
  disablePosBtn: boolean;
  isOpenPOSPanel: boolean;
  isSmallScreen?: boolean;
}

const { addDelayedAction } = StorageServices.delayedActions;

export default function BillingActionsBlock({
  profile,
  module,
  isOpenPOSPanel,
  isSmallScreen,
  disablePosBtn,
  handlePOSPanelChange,
  handleNewMemberPanelChange,
  handleChangePackagePlanChange,
}: IProps): JSX.Element {
  const dispatch = useAppDispatch();

  const classes = useStyles();
  const personId = Number(profile.get('id'));

  const selectedRegisterId: string = useRootSelector(selectSelectedRegisterId);
  const currentRegisterInvoicesList = useRootSelector(selectInvoicesForRegisterList);
  const balanceHistory = useRootSelector(selectors.selectPersonBalanceHistory(personId));
  const balanceHistoryLoading = useRootSelector(
    selectors.selectPersonBalanceHistoryLoading(personId),
  );
  const rewardsHistory = useRootSelector(selectors.selectPersonRewardsHistory(personId));
  const rewardsHistoryLoading = useRootSelector(
    selectors.selectPersonRewardsHistoryLoading(personId),
  );

  const [isBillingsScheduleModalOpen, setIsBillingsScheduleModalOpen] = useState<boolean>(false);
  const [isPastDuesModalOpen, setIsPastDuesModalOpen] = useState<boolean>(false);
  const [isPaymentMethodsModalOpen, setIsPaymentMethodsModalOpen] = useState<boolean>(false);
  const [isOpenTopUpBalanceModal, setIsOpenTopUpBalanceModal] = useState(false);
  const [isOpenTopUpRewardsModal, setIsOpenTopUpRewardsModal] = useState(false);
  const [isOpenBalanceHistoryModal, setIsOpenBalanceHistoryModal] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [isOpenRewardsHistoryModal, setIsOpenRewardsHistoryModal] = useState(false);
  const [openPosPanel, setOpenPosPanel] = useState<boolean>(false);

  const memberAlertsList: ImmutableList<IMemberAlertImt> = useRootSelector(
    selectors.selectPersonAlerts(personId),
  );

  const isPaymentMethodsNotExist = memberAlertsList?.some(
    memberAlert => memberAlert.get('condition') === AlertCondition.MissingBillingInfo,
  );

  const handleSubmitTopUpBalanceModal = (amountToPay: number) => {
    if (disablePosBtn || isOpenPOSPanel) {
      if (isOpenPOSPanel) {
        handlePOSPanelChange(false);
      }

      handleNewMemberPanelChange(false);
      handleChangePackagePlanChange(false);
    }

    setIsOpenTopUpBalanceModal(false);

    const handleCreateInvoice = (invoicesList: IInvoiceDetailsDto[], registerId: string) => {
      const openedInvoiceForSelectedCustomer: IInvoiceDetailsDto | void = invoicesList?.find(
        (invoiceItem: IInvoiceDetailsDto) => {
          return (
            invoiceItem.status === InvoiceStatus.OPEN &&
            invoiceItem.customer?.id === profile.get('id')
          );
        },
      );

      if (openedInvoiceForSelectedCustomer) {
        dispatch(
          actions.addInvoiceUnit(
            openedInvoiceForSelectedCustomer.id,
            {
              topUpBalances: [{ amount: amountToPay }],
              memberId: openedInvoiceForSelectedCustomer.customer?.id ?? null,
              registerId,
            },
            CartUnitTypes.TOP_UP_BALANCE,
            module,
            false,
            true,
          ),
        );
      } else {
        dispatch(
          actions.addInvoiceUnit(
            null,
            {
              createNewInvoice: true,
              topUpBalances: [{ amount: amountToPay }],
              memberId: personId,
              registerId,
            },
            CartUnitTypes.TOP_UP_BALANCE,
            module,
            false,
            true,
          ),
        );
      }
    };

    if (isOpenPOSPanel) {
      handleCreateInvoice(currentRegisterInvoicesList?.toJS(), selectedRegisterId);
    } else {
      addDelayedAction(ADD_TOP_UP_BALANCE, handleCreateInvoice);
    }

    setOpenPosPanel(true);
  };

  const toggleBillingsSchedule = useCallback(
    () => setIsBillingsScheduleModalOpen(prevState => !prevState),
    [],
  );

  const togglePastDues = useCallback(() => setIsPastDuesModalOpen(prevState => !prevState), []);

  const togglePaymentMethods = useCallback(
    () => setIsPaymentMethodsModalOpen(prevState => !prevState),
    [],
  );

  const handlePastDuesSubmit = useCallback(
    (selectedDues: IPastDue[]) => {
      snackbar.warning(
        `Opens a POS section to pay for ${selectedDues.length} selected dues. Not implemented yet`,
      );
      togglePastDues();
    },
    [togglePastDues],
  );

  const onOpenBalanceHistory = () => {
    setIsOpenBalanceHistoryModal(true);
  };

  const onOpenRewardsHistory = () => {
    setIsOpenRewardsHistoryModal(true);
    dispatch(fetchPersonRewardsHistory(personId, module));
  };

  const fetchBalanceHistory = useCallback(
    (tableParams: ITableParams): void => {
      dispatch(fetchPersonBalanceHistory(personId, tableParams, module));
    },
    [dispatch, personId, module],
  );

  const closePOSPanel = () => {
    setOpenPosPanel(false);
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={isSmallScreen ? 3 : 6}>
        <Button
          variant="contained"
          className={classes.selectPackage}
          onClick={() => setIsOpenTopUpBalanceModal(true)}
        >
          <FormattedMessage {...messages.topUpBalanceTitle} />
        </Button>
      </Grid>

      <Grid item xs={isSmallScreen ? 3 : 6}>
        <Button
          variant="contained"
          className={classes.selectPackage}
          onClick={onOpenBalanceHistory}
        >
          <FormattedMessage {...commonMessages.accountCreditHistoryTitle} />
        </Button>
      </Grid>

      <Grid item xs={isSmallScreen ? 3 : 6}>
        <Button
          variant="contained"
          className={classes.selectPackage}
          onClick={() => setIsOpenTopUpRewardsModal(true)}
        >
          <FormattedMessage {...messages.rewardsTitle} />
        </Button>
      </Grid>

      <Grid item xs={isSmallScreen ? 3 : 6}>
        <Button
          variant="contained"
          className={classes.selectPackage}
          onClick={onOpenRewardsHistory}
        >
          <FormattedMessage {...commonMessages.rewardsHistoryTitle} />
        </Button>
      </Grid>

      <Grid item xs={isSmallScreen ? 3 : 6}>
        <Button variant="contained" className={classes.selectPackage} onClick={togglePastDues}>
          <FormattedMessage {...messages.pastDuesModalTitle} />
        </Button>
      </Grid>

      <Grid item xs={isSmallScreen ? 3 : 6}>
        <Button
          variant="contained"
          className={classes.selectPackage}
          onClick={toggleBillingsSchedule}
        >
          <FormattedMessage {...messages.billingScheduleModalTitle} />
        </Button>
      </Grid>

      <Grid item xs={isSmallScreen ? 3 : 12}>
        <Button
          variant="contained"
          className={classes.selectPackage}
          onClick={togglePaymentMethods}
        >
          <FormattedMessage {...messages.paymentMethodsModalTitle} />
        </Button>
      </Grid>

      {openPosPanel && (
        <DialogComponent
          size="lg"
          isOpen={openPosPanel}
          title={<FormattedMessage {...messages.topUpBalanceTitle} />}
          onClose={closePOSPanel}
          hideFooterButtons
          hasCustomFooter
          className={classes.customPaper}
        >
          <PaymentStep
            personId={personId}
            disableInvoiceCreation
            disableInvoiceSelection
            onCancel={closePOSPanel}
            module={module}
            onNext={closePOSPanel}
          />
        </DialogComponent>
      )}

      {isBillingsScheduleModalOpen && (
        <BillingScheduleModal
          handlePOSPanelChange={handlePOSPanelChange}
          handleNewMemberPanelChange={handleNewMemberPanelChange}
          handleChangePackagePlanChange={handleChangePackagePlanChange}
          isOpenPOSPanel={isOpenPOSPanel}
          disablePosBtn={disablePosBtn}
          isOpen={isBillingsScheduleModalOpen}
          personId={personId}
          onSuccess={toggleBillingsSchedule}
          onCancel={toggleBillingsSchedule}
          module={module}
        />
      )}

      {isPastDuesModalOpen && (
        <PastDuesResolutionModal
          handleNewMemberPanelChange={handleNewMemberPanelChange}
          handleChangePackagePlanChange={handleChangePackagePlanChange}
          handlePOSPanelChange={handlePOSPanelChange}
          isOpenPOSPanel={isOpenPOSPanel}
          disablePosBtn={disablePosBtn}
          isOpen={isPastDuesModalOpen}
          personId={personId}
          onSubmit={handlePastDuesSubmit}
          onCancel={togglePastDues}
          module={module}
        />
      )}

      {isPaymentMethodsModalOpen && (
        <PaymentMethodsModal
          personId={personId}
          onSuccess={togglePaymentMethods}
          onCancel={togglePaymentMethods}
          alert={isPaymentMethodsNotExist ? AlertCondition.MissingBillingInfo : undefined}
          module={module}
        />
      )}

      {isOpenTopUpBalanceModal && (
        <TopUpBalanceModal
          isOpen={isOpenTopUpBalanceModal}
          onClose={() => setIsOpenTopUpBalanceModal(false)}
          handleSubmit={handleSubmitTopUpBalanceModal}
        />
      )}
      {isOpenTopUpRewardsModal && (
        <TopUpRewardsModal
          onClose={() => setIsOpenTopUpRewardsModal(false)}
          personId={personId}
          personRewards={Number(profile.get('rewardPointNumber'))}
          module={module}
        />
      )}

      {isOpenBalanceHistoryModal ? (
        <BalanceHistoryModal
          balanceHistory={balanceHistory ? balanceHistory.toJS() : undefined}
          balanceHistoryLoading={balanceHistoryLoading}
          fetchBalanceHistory={fetchBalanceHistory}
          onClose={() => setIsOpenBalanceHistoryModal(false)}
          setSelectedInvoiceId={setSelectedInvoiceId}
        />
      ) : null}

      {selectedInvoiceId ? (
        <InvoiceDetailsModal
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
          module={module}
          personId={personId}
        />
      ) : null}

      {isOpenRewardsHistoryModal && (
        <RewardsHistoryModal
          isOpen
          onClose={() => setIsOpenRewardsHistoryModal(false)}
          rewardsHistory={rewardsHistory?.toJS()}
          rewardsHistoryLoading={rewardsHistoryLoading}
        />
      )}
    </Grid>
  );
}
